import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API from '../api';
import { AuthContext } from '../components/AuthContext'; // Importa il Context
import { useEventTracking } from '../hooks/useAnalytics';

function Login() {
  const navigate = useNavigate();
  const { login, isLoggedIn } = useContext(AuthContext); // Usa la funzione `login` dal Context
  const { trackAuth } = useEventTracking();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');

  // Controlla se c'è un redirect target salvato (dopo migrazione)
  useEffect(() => {
    if (isLoggedIn) {
      const redirectTarget = localStorage.getItem('login_redirect_target');
      if (redirectTarget) {
        console.log('[Login] Trovato redirect target salvato:', redirectTarget);
        localStorage.removeItem('login_redirect_target'); // Rimuovi il target salvato
        navigate(redirectTarget);
      }
    }
  }, [isLoggedIn, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    // Traccia il tentativo di login
    trackAuth('login', 'email_password', true, 'customer');
    
    try {
      const res = await API.post('/auth/login', { email, password });
      if (res.data.success) {
        const { user, token } = res.data;

        // Traccia il login riuscito
        trackAuth('login', 'email_password', true, 'customer');

        // Aggiorna lo stato globale dell'utente tramite il Context
        login(user, token);

        // Migra la wishlist guest se esiste
        let migrationHappened = false;
        try {
          const session_id = localStorage.getItem('wishlist_session_id');
          if (session_id) {
            await API.post('/wishlist/migrate', { session_id });
            console.log('[Login] Wishlist guest migrata con successo');
            migrationHappened = true;
            // Rimuovi il session_id dopo la migrazione
            localStorage.removeItem('wishlist_session_id');
          }
        } catch (migrationError) {
          console.error('[Login] Errore nella migrazione wishlist:', migrationError);
          // Non blocchiamo il login se la migrazione fallisce
        }

        // Se è avvenuta una migrazione, salva il redirect target e ricarica
        if (migrationHappened) {
          console.log('[Login] Migrazione avvenuta, salvo il redirect target e ricarico');
          
          // Determina il redirect target
          let redirectTarget = '/';
          if (user.role === 'admin') {
            redirectTarget = '/admin/vendors';
          } else if (user.role === 'seller' && user.impersonatedBy?.role === 'admin') {
            redirectTarget = '/account';
          } else if (user.role === 'customer' || user.role === 'seller') {
            redirectTarget = '/account';
          }
          
          // Salva il redirect target nel localStorage
          localStorage.setItem('login_redirect_target', redirectTarget);
          
          // Ricarica la pagina
          window.location.reload();
          return;
        }

        // Reindirizza in base al ruolo (solo se non c'è stata migrazione)
        console.log(user);
        if (user.role === 'admin') {
          navigate('/admin/vendors');
        } else if (user.role === 'seller' && user.impersonatedBy?.role === 'admin') {
          navigate('/account');
        } else if (user.role === 'customer' || user.role === 'seller') {
          navigate('/account');
        } else {
          navigate('/');
        }
      } else {
        setMessage(res.data.message);
        
        // Traccia il login fallito
        trackAuth('login', 'email_password', false, 'customer', res.data.message);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Errore di rete o server';
      setMessage(errorMessage);
      
      // Traccia l'errore di login
      trackAuth('login', 'email_password', false, 'customer', errorMessage);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Header con icona */}
        <div className="login-header">
          <div className="login-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="10,17 15,12 10,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="15" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="login-title">Accedi al tuo account</h2>
          <p className="login-subtitle">Inserisci le tue credenziali per continuare</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-form-group">
            <label className="login-form-label">Email</label>
            <div className="login-input-wrapper">
              <svg className="login-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input 
                type="email" 
                className="login-input"
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="Inserisci la tua email"
                required
              />
            </div>
          </div>

          <div className="login-form-group">
            <label className="login-form-label">Password</label>
            <div className="login-input-wrapper">
              <svg className="login-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="16" r="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input 
                type="password" 
                className="login-input"
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Inserisci la tua password"
                required 
              />
            </div>
          </div>

          <div className="login-form-group">
            <div className="login-form-actions">
              <Link 
                to="/forgot-password" 
                className="login-forgot-link"
                onClick={() => trackAuth('click', {
                  element_type: 'link',
                  element_text: 'Password dimenticata?',
                  page_location: 'login_page',
                  destination: '/forgot-password'
                })}
              >
                Password dimenticata?
              </Link>
            </div>
          </div>

          <button type="submit" className="login-submit-btn">
            <span>Accedi</span>
            <svg className="login-submit-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="m12 5 7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </form>

        {message && (
          <div className="login-error">
            <svg className="login-error-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {message}
          </div>
        )}

        {/* Footer con link registrazione */}
        <div className="login-footer">
          <p className="login-footer-text">Non hai un account?</p>
          <Link 
            to="/sign-up-customer" 
            className="login-signup-link"
            onClick={() => trackAuth('click', {
              element_type: 'link',
              element_text: 'Registrati ora',
              page_location: 'login_page',
              destination: '/sign-up-customer'
            })}
          >
            Registrati ora
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 17 17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 7h10v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
